'use client';

import { useEffect, useMemo, useState } from 'react';
import ShowMoreText from 'react-show-more-text';
import config from 'config';
import { defaultBasketData } from 'constant';
import parse from 'html-react-parser';
import {
  useAddToShoppingCartMutation,
  useDeleteShoppingCartItemMutation,
  useGetShoppingCartQuery,
  useRemoveGiftCardPaymentMutation,
  useUpdateShoppingCartItemMutation
} from 'services/shoppingCartApi';

import {
  Box,
  Flex,
  Stack,
  Text,
  useBoolean,
  useBreakpointValue,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  addToLocalCart,
  removeFromLocalCart,
  updateLocalCartQuantity
} from 'store/reducers/cartSlice';
import { setPackagesShopLoader } from 'store/reducers/packagesShopSlice';

import { formatCurrency } from 'utils';

import {
  ExtendedLocationPackageItem,
  LocationPackageItem,
  ShopCartType
} from 'types';

import { Button, Counter, Divider } from 'ui';

const {
  header: { shoppingCartType }
} = config;

interface SubPackagesItemProps {
  item: ExtendedLocationPackageItem | LocationPackageItem;
  isInsideMasterPackage?: boolean;
  isLastItem?: boolean;
  locationId: number;
}

const SubPackageItem = ({
  item,
  isInsideMasterPackage = false,
  isLastItem = false,
  locationId
}: SubPackagesItemProps) => {
  const {
    colors: {
      palette: {
        shop: {
          packages: {
            stepPackages: { subPackages: subPackagesColors }
          }
        }
      }
    }
  } = useTheme();

  const [count, setCount] = useState<number>(0);
  const [isOpen, setOpen] = useBoolean(false);
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { selectedPackages } = useAppSelector((state) => state.cart);

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);

  const { data = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isLogged
  });
  const [addToShoppingCart] = useAddToShoppingCartMutation();
  const [deleteShoppingCartItem] = useDeleteShoppingCartItemMutation();
  const [updateShoppingCartItem] = useUpdateShoppingCartItemMutation();
  const [removeGiftCardPayment] = useRemoveGiftCardPaymentMutation();

  const removeGiftCardPaymentHandler = () => {
    if (data.payments.length > 0) {
      const pymtIds = data.payments.map((payment) => payment?.in_pymt_id);
      const deletePromises = pymtIds.map((id) =>
        removeGiftCardPayment({
          in_pymt_id: id,
          invoice_id: data.details.invoice_id ?? 0
        })
      );

      return Promise.allSettled(deletePromises);
    }
  };

  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );

  const isDiscountExist = item.price_orig > 0 && item.price_orig > item.price;
  const discountValue = item.price_orig - item.price;

  const existingShoppingCartItem = useMemo(
    () => data.items.find((shopItem) => shopItem.id === item.id),
    [data.items, item]
  );

  const existingLocalCartItem = useMemo(
    () => selectedPackages.find((shopItem) => shopItem.id === item.id),
    [selectedPackages, item]
  );

  useEffect(() => {
    if (isLogged) {
      setCount(existingShoppingCartItem?.qty ?? 0);
    } else {
      setCount(existingLocalCartItem?.qty ?? 0);
    }
  }, [existingShoppingCartItem, existingLocalCartItem, isLogged]);

  const addToShop = () => {
    if (isLogged) {
      dispatch(setPackagesShopLoader(true));

      addToShoppingCart({
        item_id: item.id,
        item_type: ShopCartType.Package,
        store_id: locationId,
        qty: 1
      })
        .unwrap()
        .then((res) => {
          if (!res?.error) {
            setCount(1);
            dispatch(setPackagesShopLoader(false));
          }
        })
        .then(removeGiftCardPaymentHandler);
    } else {
      dispatch(
        addToLocalCart({
          type: ShopCartType.Package,
          item: {
            ...item,
            qty: 1,
            store_id: locationId,
            online_fulfillment: 1
          }
        })
      );
      setCount(1);
    }
  };

  const addOnlineCartCount = (quantity: number) => {
    dispatch(setPackagesShopLoader(true));

    if (quantity === 0) {
      deleteShoppingCartItem({
        invoiceId: data.details.invoice_id ?? 0,
        cartItemId: existingShoppingCartItem?.cart_item_id ?? 0
      })
        .then(() => {
          setCount(quantity);
          dispatch(setPackagesShopLoader(false));
        })
        .then(removeGiftCardPaymentHandler);
    } else {
      updateShoppingCartItem({
        invoiceId: data.details.invoice_id ?? 0,
        cartItemId: existingShoppingCartItem?.cart_item_id ?? 0,
        itemId: existingShoppingCartItem?.id ?? 0,
        itemType: existingShoppingCartItem?.item_type ?? ShopCartType.Package,
        qty: quantity
      })
        .then(() => {
          setCount(quantity);
          dispatch(setPackagesShopLoader(false));
        })
        .then(removeGiftCardPaymentHandler);
    }
  };

  const addLocalCartCount = (quantity: number) => {
    if (quantity === 0) {
      dispatch(
        removeFromLocalCart({
          type: ShopCartType.Package,
          id: existingLocalCartItem?.id ?? 0
        })
      );
    } else {
      dispatch(
        updateLocalCartQuantity({
          type: ShopCartType.Package,
          id: existingLocalCartItem?.id ?? 0,
          qty: quantity
        })
      );
      setCount(quantity);
    }
  };

  const addCount = isLogged ? addOnlineCartCount : addLocalCartCount;

  return (
    <Stack
      w='100%'
      gap={0}
      pt={{
        base: isInsideMasterPackage ? 0 : '20px',
        md: isInsideMasterPackage ? 0 : '25px'
      }}
    >
      <Stack
        gap={0}
        pl={{
          base: isInsideMasterPackage ? '30px' : '15px',
          md: isInsideMasterPackage ? '40px' : '25px'
        }}
        pr={{ base: '15px', md: '25px' }}
      >
        <Box w='full' display={{ base: 'block', md: 'none' }} mb={2}>
          <Text fontSize='18px' fontWeight={600} lineHeight={1.2}>
            {item.name}
          </Text>
        </Box>
        <Flex justifyContent='space-between' gap='20px'>
          <Stack gap='5px'>
            <Text
              fontSize='18px'
              fontWeight={600}
              lineHeight={1.2}
              display={{ base: 'none', md: 'block' }}
            >
              {item.name}
            </Text>
            <Flex gap='5px' alignItems='center' flexWrap='wrap'>
              <Text
                mr='11px'
                fontSize={{ base: '22px', lg: '24px' }}
                fontWeight={400}
                color={subPackagesColors.priceMd}
              >
                {formatCurrency(item.price)}
              </Text>

              {isDiscountExist && (
                <Text
                  position='relative'
                  fontSize='18px'
                  color={{
                    base: subPackagesColors.discountText,
                    md: subPackagesColors.discountTextDesktop
                  }}
                  _after={{
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    bg: subPackagesColors.priceStrikethroughLine,
                    height: '1px',
                    width: 'calc(100% + 2px)',
                    bottom: '9px'
                  }}
                >
                  {formatCurrency(item.price_orig)}
                </Text>
              )}
            </Flex>

            {isDiscountExist && (
              <Text
                fontSize='14px'
                color={{
                  base: subPackagesColors.discountText,
                  md: subPackagesColors.discountTextDesktop
                }}
              >
                You are saving {formatCurrency(discountValue)}{' '}
                {isMobile ? '' : 'with this item!'}
              </Text>
            )}
          </Stack>

          <VStack justifyContent='center'>
            {count > 0 ? (
              <Counter
                minValue={0}
                maxValue={99}
                value={count}
                onChange={addCount}
                buttonProps={
                  isInsideMasterPackage
                    ? { bg: subPackagesColors.counterButtonBg }
                    : {
                        bg: subPackagesColors.buttonInsideBg,
                        _hover: {
                          bg: subPackagesColors.buttonBgHover
                        }
                      }
                }
              />
            ) : (
              <Button
                onClick={addToShop}
                variant='brand'
                colorScheme='primaryLight'
                fontSize={{ base: '16px', lg: '18px' }}
                bg={
                  isInsideMasterPackage
                    ? subPackagesColors.buttonInsideBg
                    : subPackagesColors.buttonBg
                }
                _hover={{
                  bg: isInsideMasterPackage
                    ? subPackagesColors.buttonInsideBgHover
                    : subPackagesColors.buttonBgHover,
                  opacity: 0.8
                }}
                px={{ base: '12px', lg: '19px' }}
                py='12px'
                height={{ lg: '50px' }}
                borderRadius='6px'
              >
                {`Add to ${shoppingCartType}`}
              </Button>
            )}
          </VStack>
        </Flex>

        {item.dscr_line && (
          <Box
            mt='15px'
            sx={{
              '& span:last-of-type ': {
                lineHeight: '1.6'
              },
              '& .show-more-less-clickable': {
                color: subPackagesColors.showBtn,
                paddingBottom: '1px',
                borderBottom: '1px solid',
                borderBottomColor: 'transparent',
                cursor: 'pointer',

                '&:hover': {
                  borderBottomColor: subPackagesColors.showBtn
                }
              }
            }}
          >
            <ShowMoreText
              lines={3}
              more='Show more'
              less='Show less'
              onClick={setOpen.toggle}
              expanded={isOpen}
              truncatedEndingComponent='... '
            >
              <Text
                fontSize='14px'
                lineHeight={1.6}
                color={subPackagesColors.description}
              >
                {parse(item?.dscr_line ?? '')}
              </Text>
            </ShowMoreText>
          </Box>
        )}

        {isInsideMasterPackage && !isLastItem && (
          <Divider
            borderColor={subPackagesColors.dividerInside}
            my={{ base: '15px', md: '20px' }}
            opacity={0.1}
          />
        )}
      </Stack>

      {!isInsideMasterPackage && (
        <Divider
          borderColor={subPackagesColors.divider}
          mt={{ base: '15px', md: '25px' }}
        />
      )}
    </Stack>
  );
};

export default SubPackageItem;
